import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	Store,
	StoreActionApi,
} from 'react-sweet-state';
import { createStore, createActionsHook, createHook } from 'react-sweet-state';

import { SuggestionFilterValues, SuggestionSortValues } from './suggestionsPanelFilters.ts';

export type SuggestionsPanelFiltersState = {
	currentFilter: SuggestionFilterValues;
	currentSort: SuggestionSortValues;
};

type Api = StoreActionApi<SuggestionsPanelFiltersState>;

/**
 * The filter menu lives in the object sidebar header while the list lives in the panel body, so the
 * two render in separate trees. A module level store is what lets them share the selection.
 */
const initialState: SuggestionsPanelFiltersState = {
	currentFilter: SuggestionFilterValues.ALL,
	currentSort: SuggestionSortValues.TOP_TO_BOTTOM,
};

const actions = {
	setCurrentFilter:
		(currentFilter: SuggestionFilterValues) =>
		({ setState }: Api): void => {
			setState({ currentFilter });
		},
	setCurrentSort:
		(currentSort: SuggestionSortValues) =>
		({ setState }: Api): void => {
			setState({ currentSort });
		},
	resetFilters:
		() =>
		({ setState }: Api): void => {
			setState(initialState);
		},
};

type SuggestionsPanelFiltersActions = typeof actions;

export const SuggestionsPanelFiltersStore: Store<
	SuggestionsPanelFiltersState,
	SuggestionsPanelFiltersActions
> = createStore({
	initialState,
	actions,
	name: 'SuggestionsPanelFiltersStore',
});

export const useSuggestionsPanelFilters: HookFunction<
	SuggestionsPanelFiltersState,
	BoundActions<SuggestionsPanelFiltersState, SuggestionsPanelFiltersActions>,
	void
> = createHook(SuggestionsPanelFiltersStore);

export const useSuggestionsPanelFiltersActions: HookActionsFunction<
	BoundActions<SuggestionsPanelFiltersState, SuggestionsPanelFiltersActions>
> = createActionsHook(SuggestionsPanelFiltersStore);
